import { captureUserVisibleError } from "ui/lib/sentry";
import { useCurrentUser } from "queries/current-user";
import { toast } from "react-hot-toast";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import {
  getMemberByID,
  getUser,
  getUserKEK,
  listUserWallPosts,
  searchUsers,
  toggleUserWorkoutFavorite,
  updateMember,
  User,
  UsersResponse,
} from "./services";
import {
  listUserWallPostsProps,
  MemberWallPosts,
  UserProfile,
  UserProfileDepricated,
} from "./types";
import { isUUID } from "./utils";

export const useUser = (userID: string) => {
  return useQuery<User | null>(["getUsers", userID], () => getUser(userID), {
    enabled: !!userID, // only run the query if a term is provided
  });
};

export const useUserKEK = () => {
  return useQuery(["getUserKEK"], () => getUserKEK());
};

export const useSearchUsers = (term: string) => {
  return useQuery<UsersResponse | null>(
    ["searchUsers", term],
    async () => {
      // If the term is a UUID, call getUser instead of searchUsers
      if (isUUID(term)) {
        const user = await getUser(term);
        // Return the same interface as searchUsers
        return user
          ? {
              users: [user],
            }
          : null;
      }
      // Otherwise, use the regular searchUsers function
      return searchUsers(term);
    },
    {
      enabled: !!term, // only run the query if a term is provided
    }
  );
};

export const useMemberWallPosts = (
  { memberID }: listUserWallPostsProps,
  queryProps?: UseQueryOptions<
    MemberWallPosts,
    unknown,
    MemberWallPosts,
    string[]
  >
) => {
  const queryKey = memberID ? ["listUserWallPosts", memberID] : undefined;

  const { data, error, isLoading, refetch } = useQuery(
    queryKey!,
    () => listUserWallPosts({ memberID }),
    {
      refetchOnWindowFocus: false,
      enabled: !!queryKey, // enable the query only if queryKey is not undefined
      ...queryProps,
    }
  );

  return {
    data,
    error,
    refetch,
    isLoading,
  };
};

export const useMemberMutations = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUser();

  const updateMemberMutation = useMutation(
    (data: { memberID: string; memberData: UserProfile }) =>
      updateMember(data.memberID, data.memberData),
    {
      onMutate: async (variables) => {
        const queryKey =
          currentUser?.id === variables.memberID
            ? ["/users/me"]
            : ["users", variables.memberID];
        // Snapshot the previous value
        const cachedMemberData =
          queryClient.getQueryData<UserProfile>(queryKey);

        // Optimistically update the state
        queryClient.setQueryData<any>(queryKey, {
          ...cachedMemberData,
          ...variables.memberData,
        });

        return { cachedMemberData, queryKey };
      },
      onError: (error: Error, variables, context: any) => {
        console.error("Error updating member:", error);
        toast.error(`Something went wrong`);
        captureUserVisibleError(error, {
          surface: "member_update",
          extra: {
            memberID: variables.memberID,
            fields: Object.keys(variables.memberData ?? {}),
          },
        });
        if (context?.queryKey && context?.cachedMemberData) {
          queryClient.setQueryData<UserProfile>(
            context.queryKey,
            context.cachedMemberData
          );
        }
      },
    }
  );

  return {
    updateMember: updateMemberMutation,
  };
};

export const useToggleUserWorkoutFavoriteMutation = (
  mutationProps: any = {}
) => {
  const { currentUser } = useCurrentUser();

  return useMutation(
    (data: { workoutID: string; isFavorited: boolean }) =>
      toggleUserWorkoutFavorite({ ...data, userID: currentUser?.id }),
    mutationProps
  );
};

export const useMember = (
  memberID: string,
  options?: Omit<
    UseQueryOptions<
      UserProfileDepricated,
      unknown,
      UserProfileDepricated,
      ["member", string]
    >,
    "queryKey" | "queryFn"
  >
) => {
  const { data, error, isLoading, refetch } = useQuery<
    UserProfileDepricated,
    unknown,
    UserProfileDepricated,
    ["member", string]
  >([`member`, memberID], () => getMemberByID(memberID), {
    enabled: !!memberID,
    ...options,
  });

  return {
    data,
    error,
    refetch,
    isLoading,
  };
};
